import React, { useCallback, useEffect, useState } from "react";

import { ColorPicker } from "./ColorPicker";
import ColorChooser from "./ColorChooser";
import { useColorThemes } from "@/Pages/ELearning/color-themes/useColorThemes";
import { useSelectedSimulation } from "@/hooks";
import { BrushIcon } from "lucide-react";
import { isDark } from "@/tailwind-colors/util";
import { useGetElementAsync } from "@/hooks/useGetElementAsync";
import { createPortal } from "react-dom";
import { atom } from "jotai";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@radix-ui/react-popover";

const openColorPortalAtom = atom<string | null>("");
openColorPortalAtom.debugLabel = "openColorPortalAtom";

type Props = {
    value: string;
    onChange: (value: string) => void;
    onGradientChange: (value: App.Data.DesignColorGradientData) => void;
    gradientColors?: string[];
    contentBlockId?: string;
    disallowGradients?: boolean;
    testId?: string;
};

export function ColorPickerPopover(props: Props) {
    const element = useGetElementAsync(
        `[data-content-block-id="${props.contentBlockId}"]`,
    );

    const { value, gradientColors } = props;

    const { selectedSimulation } = useSelectedSimulation();

    const initialGradient: App.Data.DesignColorGradientData = {
        id: null,
        name: "",
        angle: "r",
        simulationId: selectedSimulation?.id ?? "",
        userId: "",
        designColors:
            gradientColors?.length > 0
                ? gradientColors.map((color) => ({
                      id: "",
                      css_color: color,
                      name: "",
                      user_id: "",
                      simulation_id: selectedSimulation?.id ?? "",
                  }))
                : [
                      {
                          id: "",
                          css_color: value,
                          name: "",
                          user_id: "",
                          simulation_id: selectedSimulation?.id ?? "",
                      },
                      {
                          id: "",
                          css_color: value,
                          name: "",
                          user_id: "",
                          simulation_id: selectedSimulation?.id ?? "",
                      },
                  ],
    };

    const [gradient, setGradient] =
        useState<App.Data.DesignColorGradientData>(initialGradient);

    const [mode, setModeState] = useState<
        "color" | "gradient" | "color-chooser"
    >("color-chooser");

    const [isOpen, setIsOpen] = useState(false);

    const hasViaColor = !!gradient.designColors[2]?.css_color;

    if (element?.getBoundingClientRect()?.width < 400) {
        return (
            <ColorPickerPopoverPortal element={element} {...props}>
                <ColorPickerPopoverContents {...props} />
            </ColorPickerPopoverPortal>
        );
    }

    return (
        <div className="flex flex-col justify-center gap-2 z-50">
            <Popover>
                <PopoverTrigger
                    className="flex items-center"
                    onClick={() => setIsOpen(true)}
                >
                    <div
                        className={`flex h-7 w-7 items-center justify-center rounded-full border
                        dark:border-slate-700 border-slate-500 from-[var(--from-color)] to-[var(--to-color)] ${
                            hasViaColor ? "via-[var(--via-color)]" : ""
                        }`}
                        style={
                            {
                                backgroundColor: value,
                                "--from-color": gradientColors?.[0] || "",
                                "--to-color":
                                    gradientColors?.[
                                        gradientColors.length - 1
                                    ] || "",
                                "--via-color":
                                    gradientColors?.length > 2
                                        ? gradientColors?.[1] || ""
                                        : "",
                            } as React.CSSProperties
                        }
                        data-is-gradient={mode === "gradient"}
                        data-color={value}
                        data-testid={props.testId || "color-picker"}
                    >
                        <BrushIcon
                            aria-label="Choose color"
                            className=" h-4 w-4"
                            style={{
                                color: isDark(gradientColors?.[1] || value)
                                    ? "white"
                                    : "black",
                            }}
                        />
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    className="border-gray-300 bg-white dark:border-slate-700 dark:bg-slate-800
                        dark:text-slate-100 color-picker-popover-content min-w-60 rounded-lg p-2"
                    // static={isOpen}
                    // className="data-[open=true]:animate-fade-in data-[open=false]:animate-fade-out
                    //     data-[is-open=true]:-translate-x-full absolute z-[999999] mt-4 flex min-w-60
                    //     flex-col gap-2 rounded-lg border border-gray-300 bg-white pb-2 pt-0 shadow-md
                    //     scrollbar scrollbar-thumb-gray-500 scrollbar-thumb-rounded-full scrollbar-w-2
                    //     data-[is-open=false]:hidden"
                    // data-is-open={isOpen}
                    // style={{
                    //     transitionBehavior: "allow-discrete",
                    // }}
                >
                    <ColorPickerPopoverContents {...props} />
                </PopoverContent>
            </Popover>
            {/* {ReactDOM.createPortal(
                <button
                    className="overlay fixed inset-0 data-[is-open=false]:pointer-events-none
                        data-[is-open=true]:block data-[is-open=false]:hidden"
                    onClick={() => {
                        setIsOpen(false);
                        close();
                    }}
                    data-is-open={isOpen}
                />,
                document.body,
            )} */}
        </div>
    );
}

function ColorPickerPopoverPortal({
    children,
    element,
    gradientColors,
    value,
    disallowGradients,
}: {
    children: React.ReactNode;
    element: HTMLElement;
} & Props) {
    const hasViaColor = !!gradientColors[2];
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!!(e.target as HTMLElement).closest(".child-popover-fixed"))
                return;
            if (!element.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, [element]);

    // const isOpen =
    //     openColorPortalId === element.getAttribute("data-content-block-id");

    return (
        <>
            <button
                className={`flex h-8 w-8 items-center justify-center rounded-full border
                border-gray-300 from-[var(--from-color)] to-[var(--to-color)] ${
                    hasViaColor ? "via-[var(--via-color)]" : ""
                }`}
                style={
                    {
                        backgroundColor: value,
                        "--from-color": gradientColors?.[0] || "",
                        "--to-color":
                            gradientColors?.[gradientColors.length - 1] || "",
                        "--via-color":
                            gradientColors?.length > 2
                                ? gradientColors?.[1] || ""
                                : "",
                    } as React.CSSProperties
                }
                data-is-gradient={gradientColors.length > 1}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(true);
                    // setOpenColorPortalId(
                    //     element.getAttribute("data-content-block-id"),
                    // );
                }}
            >
                <BrushIcon
                    aria-label="Choose color"
                    className=" h-4 w-4"
                    style={{
                        color: isDark(gradientColors?.[1] || value)
                            ? "white"
                            : "black",
                    }}
                />
            </button>
            {createPortal(
                <>
                    <div
                        className={`child-popover-fixed fixed left-2 top-[6.5rem] z-[99999999] w-[432px] rounded-lg
                        bg-white p-4 opacity-0 shadow-lg transition-all duration-300
                        data-[is-open=false]:pointer-events-none data-[is-open=true]:pointer-events-auto
                        data-[is-open=true]:opacity-100 ${isOpen && "i-am-open"}`}
                        data-is-open={isOpen}
                    >
                        {children}
                    </div>
                </>,
                document.body,
            )}
        </>
    );
}

function ColorPickerTypeButton({
    children,
    onClick,
    active,
}: {
    children: React.ReactNode;
    onClick: () => void;
    active: boolean;
}) {
    return (
        <button className="grow rounded-[0.225rem] p-2 text-center text-sm transition-all duration-200
            data-[active=true]:bg-slate-700 data-[active=true]:text-slate-100 hover:bg-gray-700 dark:text-slate-200 text-slate-700"
            data-active={active}
            onClick={onClick}
        >
            {children}
        </button>
    )
}

function ColorPickerPopoverContents({
    value,
    onChange,
    onGradientChange,
    gradientColors,
    disallowGradients,
}: Props) {
    const { selectedSimulation } = useSelectedSimulation();

    const initialGradient: App.Data.DesignColorGradientData = {
        id: null,
        name: "",
        angle: "r",
        simulationId: selectedSimulation?.id ?? "",
        userId: "",
        designColors:
            gradientColors?.length > 0
                ? gradientColors.map((color) => ({
                      id: "",
                      css_color: color,
                      name: "",
                      user_id: "",
                      simulation_id: selectedSimulation?.id ?? "",
                  }))
                : [
                      {
                          id: "",
                          css_color: value,
                          name: "",
                          user_id: "",
                          simulation_id: selectedSimulation?.id ?? "",
                      },
                      {
                          id: "",
                          css_color: value,
                          name: "",
                          user_id: "",
                          simulation_id: selectedSimulation?.id ?? "",
                      },
                  ],
    };

    const [gradient, setGradient] =
        useState<App.Data.DesignColorGradientData>(initialGradient);

    const handleGradientChange = useCallback(
        (gradient: App.Data.DesignColorGradientData) => {
            setGradient(gradient);
            onGradientChange(gradient);
        },
        [onGradientChange],
    );

    const handleChange = useCallback(
        (color: string, colorIndex?: 0 | 1) => {
            if (colorIndex !== undefined) {
                const newGradient = { ...gradient };
                newGradient.designColors[colorIndex].css_color = color;
                console.log("newGradient", newGradient);
                setGradient(() => newGradient);
            } else {
                onChange(color);
            }
        },
        [gradient, onGradientChange, onChange],
    );

    const [mode, setModeState] = useState<
        "color" | "gradient" | "color-chooser"
    >("color-chooser");

    const [isOpen, setIsOpen] = useState(false);

    const setMode = useCallback(
        (mode: "color" | "gradient" | "color-chooser") => {
            setIsOpen(() => true);
            setModeState(mode);
        },
        [isOpen, mode, setModeState],
    );

    const { saveColor, saveGradient } = useColorThemes();

    return (
        <>
            <nav className="relative flex gap-2 p-2 pt-4">
                <ColorPickerTypeButton
                    onClick={() => setMode("color-chooser")}
                    active={mode === "color-chooser"}
                >
                    Palette
                </ColorPickerTypeButton>
                <ColorPickerTypeButton
                    onClick={() => setMode("color")}
                    active={mode === "color"}
                >
                    Solid
                </ColorPickerTypeButton>

                {!disallowGradients && (
                    <ColorPickerTypeButton
                        onClick={() => setMode("gradient")}
                        active={mode === "gradient"}
                    >
                        Gradient
                    </ColorPickerTypeButton>
                )}
            </nav>

            {mode === "color-chooser" ? (
                <div
                    className="max-h-96 overflow-y-auto px-2 scrollbar scrollbar-thumb-gray-500
                        scrollbar-thumb-rounded-full scrollbar-w-2"
                >
                    <ColorChooser
                        selectedColor={value}
                        selectColor={(color) => {
                            handleChange(color);
                        }}
                        selectGradient={handleGradientChange}
                    />
                </div>
            ) : (
                <div
                    className="max-h-[500px] overflow-auto scrollbar scrollbar-thumb-gray-500
                        scrollbar-thumb-rounded-full scrollbar-w-2 data-[is-gradient=true]:px-4"
                    data-is-gradient={mode === "gradient"}
                >
                    <ColorPicker
                        value={value}
                        onChange={handleChange}
                        onGradientChange={handleGradientChange}
                        isInGradientMode={mode === "gradient"}
                        initialGradient={gradient}
                    />
                </div>
            )}

            {mode !== "color-chooser" && (
                <div className="flex p-2">
                    <button
                        className="grow rounded-[0.225rem] bg-sapien-blue p-2 text-center text-xs font-semibold
                            text-white transition-all duration-200"
                        onClick={() => {
                            if (mode === "color") {
                                console.log("value", value);
                                saveColor({
                                    simulationId: selectedSimulation.id,
                                    color: value,
                                });
                            } else {
                                saveGradient({
                                    simulationId: selectedSimulation.id,
                                    colors: gradient.designColors.map(
                                        (color) => color.css_color,
                                    ),
                                    angle: gradient.angle,
                                });
                            }
                        }}
                    >
                        Add to Theme
                    </button>
                </div>
            )}
        </>
    );
}
